import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeftIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ExclamationTriangleIcon,
  CubeIcon,
  ArrowPathIcon,
  PhoneIcon,
  ClockIcon,
  ArrowTopRightOnSquareIcon,
  ArrowUpTrayIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import Button from "../../../components/controls/Button";
import DataGrid from "../../../components/Datagrid";
import FormInput from "../../../components/controls/FormInput";
import ToggleSwitch from "../../../components/ui/Toggle";
import { useAuth } from "../../../contexts/AuthContext";
import AvailabilityModal from "./ServiceAvailabilities/AvailabilityModal";

const INVENTORY_API = `${process.env.REACT_APP_API_BASE_URL}/api/inventory`;
const PRICE_TYPES_API = `${process.env.REACT_APP_API_BASE_URL}/api/PriceTypes`;

function ItemModal({ isOpen, onClose, item, type }) {
  if (!isOpen) return null;

  const isEdit = !!item;
  const title = isEdit
    ? `Modifier ${type === "product" ? "le produit" : "le service"}`
    : `Nouveau ${type === "product" ? "produit" : "service"}`;

  return (
    <div className="modal modal-open">
      <div className="modal-box max-w-2xl">
        <h3 className="font-bold text-lg flex items-center gap-2">
          <PlusIcon className="w-6 h-6" />
          {title}
        </h3>
        <div className="py-4">
          <p className="text-gray-600">
            Formulaire de {isEdit ? "modification" : "création"} d'un élément.
          </p>
        </div>
        <div className="modal-action">
          <Button
            as="button"
            type="button"
            onClick={onClose}
            color="primary"
            size="sm"
            variant="outline"
          >
            Annuler
          </Button>
          <Button
            as="button"
            type="button"
            onClick={onClose}
            color="primary"
            size="sm"
          >
            {isEdit ? "Mettre à jour" : "Créer"}
          </Button>
        </div>
      </div>
      <div className="modal-backdrop" onClick={onClose}></div>
    </div>
  );
}

export default function InventoryManagement() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const userId = currentUser.id;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [selectedIds, setSelectedIds] = useState([]);
  const [error, setError] = useState("");

  const [priceTypes, setPriceTypes] = useState([]);
  const [loadingPriceTypes, setLoadingPriceTypes] = useState(false);

  const [availabilityModal, setAvailabilityModal] = useState({
    isOpen: false,
    item: null,
  });
  const [itemModal, setItemModal] = useState({
    isOpen: false,
    item: null,
    type: "product",
  });

  const loadPriceTypes = async () => {
    setLoadingPriceTypes(true);
    try {
      const response = await fetch(PRICE_TYPES_API);
      if (!response.ok) throw new Error("Erreur chargement types de prix");
      const data = await response.json();
      setPriceTypes(data);
    } catch (err) {
      console.error("Erreur:", err);
    } finally {
      setLoadingPriceTypes(false);
    }
  };

  const loadInventory = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(
        `${INVENTORY_API}?search=${search}&type=${filterType}&userId=${userId}`
      );
      if (!response.ok) throw new Error("Erreur chargement inventaire");
      const data = await response.json();
      setItems(data);
    } catch (err) {
      console.error("Erreur:", err);
      setError("Erreur lors du chargement de l'inventaire.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInventory();
  }, [search, filterType]);

  useEffect(() => {
    loadPriceTypes();
  }, []);

  const priceTypeMapping = useMemo(() => {
    const labels = {};
    const options = priceTypes.map((type) => {
      const name = i18n.language.startsWith("fr") ? type.nameFr : type.nameEn;
      labels[type.id] = name;
      return { value: type.id, label: name };
    });
    console.log(options);
    return { labels, options };
  }, [priceTypes, i18n.language]);

  const handleCellEdit = async (itemId, field, value) => {
    try {
      let convertedValue = value;

      if (field === "stock") {
        convertedValue =
          value === "" || value === null ? null : parseInt(value, 10);
        if (
          convertedValue !== null &&
          (isNaN(convertedValue) || convertedValue < 0)
        ) {
          alert("Veuillez entrer un nombre entier positif valide");
          return false;
        }
      }

      if (field === "price") {
        convertedValue =
          value === "" || value === null ? null : parseFloat(value);
        if (
          convertedValue !== null &&
          (isNaN(convertedValue) || convertedValue < 0)
        ) {
          alert("Veuillez entrer un prix valide");
          return false;
        }
      }

      if (field === "priceType") {
        convertedValue =
          value === "" || value === null ? null : parseInt(value, 10);
      }

      if (field === "isActive") {
        convertedValue = Boolean(value);
      }

      const response = await fetch(`${INVENTORY_API}/${itemId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [field]: convertedValue }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("Erreur serveur:", errorData);
        throw new Error(errorData.title || "Erreur de sauvegarde");
      }

      setItems((prev) =>
        prev.map((item) =>
          item.id === itemId ? { ...item, [field]: convertedValue } : item
        )
      );
      return true;
    } catch (error) {
      console.error("Erreur:", error);
      alert("Erreur lors de la sauvegarde: " + error.message);
      return false;
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedIds.length === 0) return;

    const confirmMsg =
      action === "delete"
        ? `Supprimer ${selectedIds.length} élément(s) ?`
        : `${action === "activate" ? "Activer" : "Désactiver"} ${
            selectedIds.length
          } élément(s) ?`;

    if (!window.confirm(confirmMsg)) return;

    try {
      const response = await fetch(`${INVENTORY_API}/bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: selectedIds, action }),
      });
      if (!response.ok) throw new Error("Erreur action groupée");

      loadInventory();
      setSelectedIds([]);
    } catch (error) {
      console.error("Erreur:", error);
      alert("Erreur lors de l'action groupée");
    }
  };

  const openAvailabilityModal = (item) => {
    setAvailabilityModal({ isOpen: true, item });
  };

  const openNewItemModal = (type = "product") => {
    setItemModal({ isOpen: true, item: null, type });
  };

  const openEditItemModal = (item) => {
    setItemModal({ isOpen: true, item, type: item.itemType });
  };

  const columns = useMemo(
    () => [
      {
        key: "itemType",
        label: "Type",
        width: "120px",
        editable: false,
        format: (value) => {
          const translations = {
            product: "Produit",
            service: "Service",
          };
          return translations[value] || value;
        },
      },
      {
        key: "internalReference",
        label: "Réf. interne",
        width: "120px",
        editable: false,
        type: "text",
        format: (value) => value || <span className="text-gray-400">—</span>,
      },
      {
        key: "name",
        label: "Nom",
        width: "250px",
        editable: false,
        type: "text",
      },
      {
        key: "price",
        label: "Prix",
        width: "100px",
        editable: true,
        type: "number",
        step: "0.01",
        min: "0",
        format: (value) => `${value?.toFixed(2) || "0.00"} $`,
      },

      {
        key: "priceType",
        label: "Type prix",
        width: "120px",
        editable: true,
        type: "select",
        options: priceTypeMapping.options,
        format: (value) => priceTypeMapping.labels[value] || "—",
      },
      {
        key: "stock",
        label: "Stock",
        width: "120px",
        render: (row, { startEdit, isEditing }) => {
          if (row.itemType === "service") {
            return;
          }

          if (row.inventoryMode === "unlimited") {
            return (
              <span className="text-gray-500 flex items-center gap-1">
                <ArrowPathIcon className="w-5 h-5" />
                <span className="text-xs">∞</span>
              </span>
            );
          }

          if (isEditing) {
            return null;
          }

          const isLowStock = row.stock < 5 && row.inventoryMode === "fixed";
          return (
            <div
              className="flex items-center gap-2 cursor-pointer hover:bg-base-200 p-2 rounded transition-colors"
              onClick={startEdit}
              title="Cliquer pour modifier le stock"
            >
              <span
                className={isLowStock ? "text-orange-600 font-semibold" : ""}
              >
                {row.stock}
              </span>
              {isLowStock && (
                <ExclamationTriangleIcon
                  className="w-4 h-4 text-orange-600"
                  title="Stock faible"
                />
              )}
            </div>
          );
        },
        editable: true,
        type: "number",
        min: "0",
      },
      {
        key: "inventoryMode",
        label: "Mode",
        width: "140px",
        render: (row) => {
          if (row.itemType === "service") {
            return (
              <Button
                as="button"
                type="button"
                onClick={() => openAvailabilityModal(row)}
                color="primary"
                size="sm"
                variant="outline"
                className="flex items-center gap-1"
                rightIcon={<ClockIcon className="w-4 h-4" />}
              >
                Horaires
              </Button>
            );
          }

          const modes = {
            fixed: { icon: CubeIcon, label: "Fixe" },
            unlimited: { icon: ArrowPathIcon, label: "Illimité" },
            contact: { icon: PhoneIcon, label: "Contact" },
          };

          const mode = modes[row.inventoryMode];
          if (!mode) return "—";

          const Icon = mode.icon;
          return (
            <span className="flex items-center gap-1">
              <Icon className="w-4 h-4" />
              {mode.label}
            </span>
          );
        },
        editable: true,
        type: "select",
        options: [
          { value: "fixed", label: "Fixe" },
          { value: "unlimited", label: "Illimité" },
          { value: "contact", label: "Contact" },
        ],
      },
      {
        key: "isActive",
        label: "Actif",
        width: "100px",
        cellClassName: "text-center",
        render: (row) => (
          <ToggleSwitch
            isEnabled={row.isActive}
            onToggle={async () => {
              const newValue = !row.isActive;
              await handleCellEdit(row.id, "isActive", newValue);
            }}
            labelOn=""
            labelOff=""
            className="toggle-sm"
          />
        ),
      },
      {
        key: "actions",
        label: "Actions",
        width: "120px",
        render: (row) => (
          <button
            className="flex items-center gap-1"
            onClick={() => {
              if (row.itemType === "product") {
                navigate(`/provider-dashboard/products/${row.id}`);
              } else {
                navigate(`/provider-dashboard/services/${row.id}`);
              }
            }}
          >
            <ArrowTopRightOnSquareIcon className="w-4 h-4" />
          </button>
        ),
      },
    ],
    [handleCellEdit, navigate, openAvailabilityModal, priceTypeMapping]
  );

  const handleExportCSV = () => {
    const csvData = items.map((item) => ({
      Type: item.itemType === "product" ? "Produit" : "Service",
      "Réf. interne": item.internalReference || "",
      SKU: item.sku || "",
      Nom: item.name,
      Prix: item.price?.toFixed(2) || "0.00",
      "Type de prix": priceTypeMapping.labels[item.priceType] || "Inconnu",
      Stock: item.stock || "",
      "Mode inventaire": item.inventoryMode || "",
      Actif: item.isActive ? "Oui" : "Non",
    }));

    const headers = Object.keys(csvData[0] || {});
    const csvContent = [
      headers.join(","),
      ...csvData.map((row) =>
        headers
          .map((header) => {
            const value = row[header] || "";
            return `"${String(value).replace(/"/g, '""')}"`;
          })
          .join(",")
      ),
    ].join("\n");

    const blob = new Blob(["\ufeff" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `inventaire_${new Date().toISOString().split("T")[0]}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="p-6 space-y-6 text-primary">
      <Button
        as="button"
        type="button"
        onClick={() => navigate(-1)}
        color="primary"
        size="md"
        className="rounded-full flex items-center gap-2"
        leftIcon={<ArrowLeftIcon className="w-5 h-5" />}
      >
        {t("global.back")}
      </Button>

      <div className="flex flex-col md:flex-row justify-between items-center gap-4">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <CubeIcon className="w-7 h-7" />
          Gestion d'inventaire et disponibilités
        </h1>
        <div className="relative w-full md:w-80">
          <FormInput
            placeholder="Rechercher..."
            name="search"
            value={search}
            onChange={(name, value) => setSearch(value)}
            type="text"
            icon={<MagnifyingGlassIcon className="w-5 h-5 text-gray-400" />}
            showRequiredOrOptional={false}
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <FunnelIcon className="w-5 h-5 text-gray-500" />
        <div className="btn-group">
          <button
            onClick={() => setFilterType("all")}
            className={`btn btn-sm m-2 ${
              filterType === "all" ? "btn-active" : ""
            }`}
          >
            Tous
          </button>
          <button
            onClick={() => setFilterType("products")}
            className={`btn btn-sm mr-2 ${
              filterType === "products" ? "btn-active" : ""
            }`}
          >
            Produits
          </button>
          <button
            onClick={() => setFilterType("services")}
            className={`btn btn-sm ${
              filterType === "services" ? "btn-active" : ""
            }`}
          >
            Services
          </button>
        </div>
        <div className="flex-1" />
        <Button
          as="button"
          type="button"
          onClick={handleExportCSV}
          color="primary"
          size="sm"
          variant="outline"
          rightIcon={<ArrowUpTrayIcon className="w-4 h-4 mr-2" />}
        >
          Exporter CSV
        </Button>
      </div>

      {loading || loadingPriceTypes ? (
        <div className="text-center py-8">Chargement de l'inventaire...</div>
      ) : error ? (
        <div className="text-center text-red-500 py-8">{error}</div>
      ) : (
        <DataGrid
          columns={columns}
          data={items}
          onCellEdit={handleCellEdit}
          loading={loading}
          emptyMessage="Aucun produit ou service trouvé"
          rowSelection={true}
          onSelectionChange={setSelectedIds}
        />
      )}

      <div className="text-right text-sm text-gray-600">
        {items.length} élément(s) au total
      </div>

      <AvailabilityModal
        isOpen={availabilityModal.isOpen}
        onClose={() => setAvailabilityModal({ isOpen: false, item: null })}
        item={availabilityModal.item}
      />

      <ItemModal
        isOpen={itemModal.isOpen}
        onClose={() =>
          setItemModal({ isOpen: false, item: null, type: "product" })
        }
        item={itemModal.item}
        type={itemModal.type}
      />
    </div>
  );
}
